import { Close } from "@mui/icons-material";
import { Box, css, IconButton, styled, Typography } from "@mui/material";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";

import { TOOLBAR_COLORS } from "components/toolbars/constants";
import { useDeviceOrientation } from "contexts/panelMode";
import { PanelPickerOverlayContext } from "contexts/panelPickerOverlay";

import {
    DRAWER_ANIMATION_DURATION,
    DRAWER_COLORS,
    DRAWER_HEIGHT_RATIO,
    DRAWER_LAYOUT,
    DRAWER_WIDTH,
    DRAWER_Z_INDEX,
} from "./constants";

const DISMISS_THRESHOLD = 0.3;

// Portrait: Full width, 3/4 height, from bottom
const PortraitDrawerContainer = styled(Box)<{ isOpen: boolean }>(
    ({ isOpen }) => css`
        position: fixed;
        left: 0;
        right: 0;
        bottom: 0;
        height: ${DRAWER_HEIGHT_RATIO * 100}vh;
        background-color: ${DRAWER_COLORS.BG};
        border-radius: 16px 16px 0 0;
        box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
        display: flex;
        flex-direction: column;
        overflow: hidden;
        z-index: ${DRAWER_Z_INDEX};
        pointer-events: auto;
        overscroll-behavior: none;
        transition: transform ${DRAWER_ANIMATION_DURATION}ms ease-out;
        transform: translateY(${isOpen ? "0" : "100%"});
    `,
);

// Landscape: Fixed width, 12px from right edge, from bottom to top bar
const LandscapeDrawerContainer = styled(Box)<{ isOpen: boolean }>(
    ({ isOpen }) => css`
        position: fixed;
        right: ${DRAWER_LAYOUT.RIGHT_MARGIN}px;
        top: ${DRAWER_LAYOUT.TOP_BAR_HEIGHT}px;
        bottom: 0;
        width: ${DRAWER_WIDTH}px;
        background-color: ${DRAWER_COLORS.BG};
        border-radius: 16px 16px 0 0;
        box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
        display: flex;
        flex-direction: column;
        overflow: hidden;
        z-index: ${DRAWER_Z_INDEX};
        pointer-events: auto;
        transition: transform ${DRAWER_ANIMATION_DURATION}ms ease-out;
        transform: translateY(${isOpen ? "0" : "100%"});
    `,
);

const DragHandle = styled(Box)`
    width: 40px;
    height: 4px;
    border-radius: 2px;
    background-color: ${DRAWER_COLORS.BORDER};
    margin: 8px auto 0;
    flex-shrink: 0;
    position: relative;
    z-index: 11;
`;

const DrawerHeader = styled(Box)(
    () => css`
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 12px 16px;
        background-color: ${DRAWER_COLORS.HEADER_BG};
        min-height: 48px;
        flex-shrink: 0;
    `,
);

const HeaderLeft = styled(Box)`
    display: flex;
    align-items: center;
    gap: 8px;
    color: ${DRAWER_COLORS.TEXT};
`;

const HeaderIcon = styled(Box)`
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
        width: 20px;
        height: 20px;
        color: ${DRAWER_COLORS.ICON};
    }
`;

const CloseButton = styled(IconButton)`
    padding: 8px;
    background-color: ${DRAWER_COLORS.BUTTON_BG};
    border-radius: 8px;
    color: ${DRAWER_COLORS.ICON};

    &:hover {
        background-color: ${TOOLBAR_COLORS.HOVER_DARK};
    }

    svg {
        width: 20px;
        height: 20px;
    }
`;

const DrawerBody = styled(Box)(
    () => css`
        flex: 1;
        min-height: 0;
        overflow: auto;
        background-color: ${DRAWER_COLORS.BG};
        -webkit-overflow-scrolling: touch;
    `,
);

const Overlay = styled(Box)<{ isOpen: boolean }>(
    ({ isOpen }) => css`
        position: fixed;
        inset: 0;
        background-color: ${DRAWER_COLORS.OVERLAY_BG};
        z-index: ${DRAWER_Z_INDEX - 1};
        pointer-events: ${isOpen ? "auto" : "none"};
        opacity: ${isOpen ? 1 : 0};
        transition: opacity ${DRAWER_ANIMATION_DURATION}ms ease-out;
    `,
);

export interface DrawerComponentProps {
    /** Drawer title */
    title: string;
    /** Optional subtitle shown below the title (e.g. selected object name) */
    subtitle?: string;
    /** Optional icon */
    icon?: ReactNode;
    /** Drawer content */
    children: ReactNode;
    /** Whether drawer is open */
    isOpen: boolean;
    /** Close handler */
    onClose: () => void;
    /**
     * When true, the drag handle and internal header (title, close button) are not rendered.
     * Use when the drawer content supplies its own header.
     */
    hideHeader?: boolean;
    /** Optional test ID */
    "data-testid"?: string;
}

/**
 * Drawer Component
 *
 * A panel that slides in from the edge of the screen for phone UI.
 * - Portrait: slides up from bottom, full width, 3/4 height
 * - Landscape: slides in from right, 375px width, full height below top bar
 */
export function DrawerComponent({
    title,
    subtitle,
    icon,
    children,
    isOpen,
    onClose,
    hideHeader = false,
    "data-testid": dataTestId,
}: DrawerComponentProps) {
    const orientation = useDeviceOrientation();
    const isPortrait = orientation === "portrait";

    const DrawerContainer = isPortrait ? PortraitDrawerContainer : LandscapeDrawerContainer;

    const [pickerOverlay, setPickerOverlay] = useState<ReactNode>(null);
    const overlayContextValue = useMemo(() => ({ setOverlay: setPickerOverlay }), []);

    const containerRef = useRef<HTMLDivElement>(null);
    const dragStartY = useRef<number | null>(null);
    const dragOffset = useRef(0);
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    useEffect(() => {
        const el = containerRef.current;
        if (!el) {
            return;
        }

        const handleTouchStart = (e: TouchEvent) => {
            dragStartY.current = e.touches[0].clientY;
            dragOffset.current = 0;
            el.style.transition = "none";
        };

        const handleTouchMove = (e: TouchEvent) => {
            if (dragStartY.current === null) {
                return;
            }
            const delta = e.touches[0].clientY - dragStartY.current;
            if (delta > 0) {
                e.preventDefault();
            }
            dragOffset.current = Math.max(0, delta);
            el.style.transform = `translateY(${dragOffset.current}px)`;
        };

        const handleTouchEnd = () => {
            if (dragStartY.current === null) {
                return;
            }
            const size = el.offsetHeight;
            el.style.transition = `transform ${DRAWER_ANIMATION_DURATION}ms ease-out`;

            if (dragOffset.current > size * DISMISS_THRESHOLD) {
                el.style.transform = "translateY(100%)";
                setTimeout(() => onCloseRef.current(), DRAWER_ANIMATION_DURATION);
            } else {
                el.style.transform = "translateY(0)";
            }

            dragStartY.current = null;
            dragOffset.current = 0;
        };

        el.addEventListener("touchstart", handleTouchStart, { passive: true });
        el.addEventListener("touchmove", handleTouchMove, { passive: false });
        el.addEventListener("touchend", handleTouchEnd, { passive: true });
        el.addEventListener("touchcancel", handleTouchEnd, { passive: true });

        return () => {
            el.removeEventListener("touchstart", handleTouchStart);
            el.removeEventListener("touchmove", handleTouchMove);
            el.removeEventListener("touchend", handleTouchEnd);
            el.removeEventListener("touchcancel", handleTouchEnd);
        };
    }, []);

    return (
        <PanelPickerOverlayContext.Provider value={overlayContextValue}>
            <Overlay isOpen={isOpen} onClick={onClose} />
            <DrawerContainer ref={containerRef} isOpen={isOpen} data-testid={dataTestId}>
                {!hideHeader && (
                    <>
                        <DragHandle />
                        <DrawerHeader className="drawer-header">
                            <HeaderLeft>
                                {icon && <HeaderIcon>{icon}</HeaderIcon>}
                                <Box display="flex" flexDirection="column">
                                    <Typography
                                        variant={subtitle ? "caption" : "subtitle1"}
                                        fontWeight={600}
                                        color={subtitle ? "text.secondary" : undefined}
                                    >
                                        {title}
                                    </Typography>
                                    {subtitle && (
                                        <Typography variant="subtitle1" fontWeight={600}>
                                            {subtitle}
                                        </Typography>
                                    )}
                                </Box>
                            </HeaderLeft>
                            <CloseButton onClick={onClose} size="small" title="Close drawer">
                                <Close />
                            </CloseButton>
                        </DrawerHeader>
                    </>
                )}
                <DrawerBody>{children}</DrawerBody>
                {pickerOverlay !== null && (
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            zIndex: 10,
                            bgcolor: "white",
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        {pickerOverlay}
                    </Box>
                )}
            </DrawerContainer>
        </PanelPickerOverlayContext.Provider>
    );
}
